import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Container } from 'react-bootstrap'
import { FaLinkedinIn, FaInstagram, FaEnvelope } from 'react-icons/fa'
import Navigation from '../../components/navigation/Navigation'
import Footer from '../../components/footer/Footer'
import Banner from '../../components/banner/banner'
import SEO from '../../components/seo/SEO'
import { EXPERTISEBG } from '../../assets/images'
import blogPosts from '../../components/blog_files/blogs-data.json'
import './blog.css'

const XLogo = () => <span style={{ fontWeight: 700 }}>X</span>

const SHARE = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/triumphal-tech-solutions/', Icon: FaLinkedinIn },
  { label: 'X', href: 'https://x.com/triumphal_io', Icon: XLogo },
  { label: 'Instagram', href: 'https://www.instagram.com/triumphal.io/', Icon: FaInstagram },
  { label: 'Email', href: 'mailto:info@triumphal.io', Icon: FaEnvelope },
]

const textOf = (node) =>
  typeof node === 'string'
    ? node
    : Array.isArray(node)
      ? node.map(textOf).join('')
      : textOf(node?.props?.children ?? '')
const idOf = (text) => text.toLowerCase().replace(/[^\w]+/g, '-').replace(/^-|-$/g, '')

const headingsOf = (content) =>
  content
    .split('\n')
    .map((line) => /^#{1,3}\s+(.+?)\s*#*$/.exec(line)?.[1])
    .filter(Boolean)
    .map((t) => t.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/[*_`]/g, ''))

const heading = (Tag) =>
  function Heading({ children }) {
    return <Tag id={idOf(textOf(children))}>{children}</Tag>
  }
const headingComponents = { h1: heading('h1'), h2: heading('h2'), h3: heading('h3') }

const BlogPost = () => {
  const { slug } = useParams()
  const post = blogPosts.find((item) => item.slug === slug)
  const toc = useMemo(() => (post ? headingsOf(post.content) : []), [post])
  const [active, setActive] = useState('')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [slug])

  // Highlight the section being read: the last heading that has reached the top of the window.
  useEffect(() => {
    const update = () => {
      let current = ''
      for (const text of toc) {
        const el = document.getElementById(idOf(text))
        if (el && el.getBoundingClientRect().top <= 140) current = idOf(text)
        else if (el) break
      }
      setActive(current)
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [toc])

  if (!post) {
    return <Navigate to="/blog" replace />
  }

  const current = active || (toc[0] ? idOf(toc[0]) : '')
  const jump = (text) => {
    setActive(idOf(text))
    document.getElementById(idOf(text))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <>
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        keywords={post.tags.join(', ')}
      />
      <Navigation />
      <Banner title="Blog" bg={EXPERTISEBG} />
      <Container className="mt-75 mb-75">
        <article className="blog_post">
          <Link to="/blog" className="blog_back_link">
            <span className="blog_back_arrow">←</span>
            All articles
          </Link>

          <p className="blog_mono blog_post_kicker">
            {post.category && <span className="blog_card_category">{post.category}</span>}
            {post.category && post.date && <span className="blog_card_slash"> / </span>}
            {post.date && <span>{post.date}</span>}
          </p>
          <h1 className="blog_post_title">{post.title}</h1>
          {post.subtitle && <p className="blog_post_subtitle">{post.subtitle}</p>}
          <p className="blog_mono blog_post_readtime">{post.readTime}</p>

          <div className="blog_post_layout">
            <aside className="blog_post_side">
              <div className="blog_post_author">
                <span className="blog_avatar">
                  {(post.author || '?').trim().charAt(0).toUpperCase()}
                </span>
                <span className="blog_post_author_name">{post.author}</span>
              </div>
              <div className="blog_post_share">
                <p className="blog_mono blog_share_label">Share this article</p>
                <div className="blog_share_buttons">
                  {SHARE.map(({ label, href, Icon }) => (
                    <a key={label} href={href} target="_blank" rel="noopener noreferrer" title={label} aria-label={label}>
                      <Icon />
                    </a>
                  ))}
                </div>
              </div>
            </aside>

            <div className="blog_post_main">
              {post.overviewImage && (
                <img className="blog_post_overview_image" src={post.overviewImage} alt={post.title} />
              )}
              <div className="blog_post_content">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={headingComponents}>
                  {post.content}
                </ReactMarkdown>
              </div>
              {post.tags.length > 0 && (
                <div className="blog_post_tags">
                  {post.tags.map((tag) => (
                    <span key={tag} className="blog_post_tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {toc.length > 0 && (
              <nav className="blog_post_toc" aria-label="On this page">
                <p className="blog_mono blog_share_label">On this page</p>
                <ul>
                  {toc.map((text) => (
                    <li key={text}>
                      <button
                        type="button"
                        onClick={() => jump(text)}
                        className={current === idOf(text) ? 'active' : ''}
                      >
                        {text}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </article>
      </Container>
      <Footer />
    </>
  )
}

export default BlogPost
